import React, { useRef, useCallback } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { QuestionLayout } from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useMenstrualHealth } from "../MenstrualHealthContext";
import { buildMenstrualHealthNavigationStack } from "../buildMenstrualHealthNavigationStack";
import type { MenstrualHealthStackParamList } from "../MenstrualHealthNavigator";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const MIN = 1;
const MAX = 10;
const STEPS = MAX - MIN;
const THUMB_SIZE = 32;

export function PainScaleScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("PainScaleScreen");
  const { state, updateState, save } = useMenstrualHealth();
  const navigation =
    useNavigation<NavigationProp<MenstrualHealthStackParamList>>();
  const trackRef = useRef<View>(null);
  const theme = useMedicalHistoryTheme();

  const value = state.painScale;

  const valueFromEvent = useCallback((pageX: number) => {
    return new Promise<number>((resolve) => {
      const el = trackRef.current;
      if (!el) return;
      // Works on both web and native
      el.measureInWindow((x: number, _y: number, width: number) => {
        const relative = pageX - x;
        const clamped = Math.max(0, Math.min(relative, width));
        const raw = MIN + (clamped / width) * STEPS;
        resolve(Math.round(raw));
      });
    });
  }, []);

  const handlePress = useCallback(
    async (pageX: number) => {
      const v = await valueFromEvent(pageX);
      updateState({ painScale: v });
    },
    [valueFromEvent, updateState],
  );

  const handleSave = async () => {
    await save();
    const stack = buildMenstrualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("PainScale");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const fraction = value != null ? (value - MIN) / STEPS : 0;

  return (
    <QuestionLayout
      title={t("med_history.gynae.menstrual_health.pain_scale_title")}
      subtitle={t("med_history.gynae.menstrual_health.pain_scale_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={state.painScale === null}
      saveLabel={t("med_history.gynae.menstrual_health.continue")}
    >
      <View style={styles.container}>
        {/* Current value display */}
        <View style={styles.valueDisplay}>
          <Text style={[styles.valueText, { color: theme.accentLight }]}>
            {value ?? "–"}
          </Text>
        </View>

        {/* Slider track */}
        <View style={styles.sliderWrapper}>
          <View
            ref={trackRef}
            style={styles.track}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderGrant={(e) => handlePress(e.nativeEvent.pageX)}
            onResponderMove={(e) => handlePress(e.nativeEvent.pageX)}
          >
            {/* Track background */}
            <View
              style={[
                styles.trackBackground,
                { backgroundColor: theme.inputBorder },
              ]}
            />
            {/* Filled portion */}
            <View
              style={[
                styles.trackFilled,
                {
                  width: value != null ? `${fraction * 100}%` : "0%",
                  backgroundColor: theme.accentLight,
                },
              ]}
            />
            {/* Thumb */}
            {value != null && (
              <View
                style={[
                  styles.thumb,
                  {
                    left: `${fraction * 100}%`,
                    marginLeft: -THUMB_SIZE / 2,
                    backgroundColor: theme.accentLight,
                    shadowColor: theme.accentLight,
                  },
                ]}
              >
                <Text
                  style={[styles.thumbText, { color: theme.completeButtonBg }]}
                >
                  {value}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Tap targets for precise selection */}
        <View style={styles.tickTapRow}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
            <Pressable
              key={num}
              style={styles.tickTap}
              onPress={() => {
                capturePress(pressLabel(String(num)), "pain_scale");
                updateState({ painScale: num });
              }}
            >
              <Text
                style={[
                  styles.tickLabel,
                  {
                    color: value === num ? theme.accentLight : theme.textFaint,
                  },
                  ...(value === num ? [{ fontWeight: "700" as const }] : []),
                ]}
              >
                {num}
              </Text>
            </Pressable>
          ))}
        </View>

        <View style={styles.labelRow}>
          <Text style={{ fontSize: 12, color: theme.textFaint }}>
            {t("med_history.gynae.menstrual_health.pain_no_pain")}
          </Text>
          <Text style={{ fontSize: 12, color: theme.textFaint }}>
            {t("med_history.gynae.menstrual_health.pain_worst_pain")}
          </Text>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  valueDisplay: {
    alignItems: "center",
    marginBottom: 24,
  },
  valueText: {
    fontSize: 48,
    fontWeight: "700",
  },
  sliderWrapper: {
    paddingHorizontal: THUMB_SIZE / 2,
    marginBottom: 8,
  },
  track: {
    height: 44,
    justifyContent: "center",
    position: "relative",
    cursor: "pointer",
  },
  trackBackground: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 6,
    borderRadius: 3,
  },
  trackFilled: {
    position: "absolute",
    left: 0,
    height: 6,
    borderRadius: 3,
  },
  thumb: {
    position: "absolute",
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    alignItems: "center",
    justifyContent: "center",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 4,
  },
  thumbText: {
    fontSize: 13,
    fontWeight: "700",
  },
  tickTapRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  tickTap: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 4,
  },
  tickLabel: {
    fontSize: 11,
    fontWeight: "500",
    textAlign: "center",
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 4,
  },
});
